import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addMonths, eachDayOfInterval, endOfMonth, format, formatDistanceToNowStrict, startOfMonth, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarClock, ChevronLeft, ChevronRight, Clock, DoorOpen, Mail, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { clinicDateKey, clinicTime, dateFromKey } from '../utils/clinicTime';

type SlotState = 'free' | 'pending' | 'confirmed' | 'completed';

interface Appt {
  id: number; status: string; patientName: string; patientPhone: string | null; patientEmail: string | null;
  holdExpiresAt: string | null; source: string; answers: Array<{ label: string; value: string }>; notes: string | null;
}
interface Slot { date: string; time: string; endTime: string; roomName: string; state: SlotState; appointment?: Appt }
interface Day {
  date: string;
  rentals: Array<{ id: number; roomName: string; start: string; end: string; activeAppointments: number }>;
  slots: Slot[]; other: Slot[];
  counts: { free: number; pending: number; confirmed: number; completed: number };
}
interface Agenda { durationMinutes: number; days: Day[] }

const upper = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const STATE: Record<SlotState, { label: string; box: string; badge: string }> = {
  free: { label: 'Libre', box: 'border-dashed border-gray-300 bg-white', badge: 'bg-gray-100 text-gray-600' },
  pending: { label: 'Esperando aprobación', box: 'border-amber-300 bg-amber-50', badge: 'bg-amber-100 text-amber-800' },
  confirmed: { label: 'Confirmado', box: 'border-green-300 bg-green-50', badge: 'bg-green-100 text-green-800' },
  completed: { label: 'Realizado', box: 'border-gray-200 bg-gray-50', badge: 'bg-primary-100 text-primary-800' },
};

const nowKey = () => `${clinicDateKey(new Date())}T${clinicTime(new Date())}`;

const SlotRow: React.FC<{ slot: Slot; onChanged: () => void; professionalId?: number | string; admin?: boolean }> = ({ slot, onChanged, professionalId, admin }) => {
  const st = STATE[slot.state];
  const a = slot.appointment;
  const over = `${slot.date}T${slot.endTime}` <= nowKey();            // the slot has already ended
  const upcoming = `${slot.date}T${slot.time}` > nowKey();
  const [booking, setBooking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const [err, setErr] = useState('');

  const markDone = async () => {
    if (!a) return;
    setBusy(true);
    try {
      await api.put(`/appointments/${a.id}/status`, { status: 'completed' });
      toast.success('Turno marcado como realizado');
      onChanged();
    } catch (e: any) { toast.error(e.response?.data?.error || 'No se pudo actualizar el turno'); } finally { setBusy(false); }
  };

  const bookPatient = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    if (form.name.trim().length < 2) { setErr('Ingresá el nombre del paciente'); return; }
    setBusy(true);
    try {
      await api.post('/postgres/professionals/agenda/book', {
        date: slot.date, time: slot.time, patient_name: form.name.trim(), professional_id: professionalId,
        patient_phone: form.phone || undefined, patient_email: form.email || undefined, notes: form.notes || undefined,
      });
      toast.success('Paciente agendado y confirmado');
      setBooking(false); setForm({ name: '', phone: '', email: '', notes: '' });
      onChanged();
    } catch (er: any) {
      setErr(er.response?.data?.error || 'No pudimos agendar el turno');
      if (er.response?.status === 409) onChanged();
    } finally { setBusy(false); }
  };

  return (
    <li className={`rounded-xl border p-4 ${st.box}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold text-gray-900">
          <Clock className="h-4 w-4 text-gray-500" aria-hidden="true" />
          {slot.time}<span className="text-gray-400">–</span>{slot.endTime}
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-bold ${st.badge}`}>{st.label}</span>
      </div>
      <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-500"><DoorOpen className="h-4 w-4" aria-hidden="true" />{slot.roomName}</p>

      {a && (
        <div className="mt-3 border-t border-black/5 pt-3">
          <p className="text-base font-semibold text-gray-900">{a.patientName}</p>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
            {a.patientPhone && <a className="inline-flex items-center gap-1.5 hover:underline" href={`tel:${a.patientPhone.replace(/\s/g, '')}`}><Phone className="h-4 w-4" aria-hidden="true" />{a.patientPhone}</a>}
            {a.patientEmail && <a className="inline-flex items-center gap-1.5 hover:underline" href={`mailto:${a.patientEmail}`}><Mail className="h-4 w-4" aria-hidden="true" />{a.patientEmail}</a>}
          </div>
          {a.answers.length > 0 && (
            <dl className="mt-2 grid gap-1 text-sm">
              {a.answers.map((x, i) => (
                <div key={i} className="flex flex-wrap gap-x-2"><dt className="font-medium text-gray-700">{x.label}:</dt><dd className="text-gray-600">{x.value}</dd></div>
              ))}
            </dl>
          )}
          {a.notes && <p className="mt-2 text-sm text-gray-600"><span className="font-medium text-gray-700">Notas:</span> {a.notes}</p>}
          {slot.state === 'pending' && (
            <p className="mt-2 text-sm text-amber-800">
              El consultorio todavía tiene que aprobar esta reserva
              {a.holdExpiresAt && ` (se libera ${formatDistanceToNowStrict(new Date(a.holdExpiresAt), { locale: es, addSuffix: true })} si no hay respuesta)`}.
            </p>
          )}
          {slot.state === 'confirmed' && over && (
            <button type="button" className="btn btn-sm btn-primary mt-3" disabled={busy} onClick={markDone}>Marcar realizado</button>
          )}
        </div>
      )}

      {slot.state === 'free' && upcoming && !booking && (
        <button type="button" className="btn btn-sm btn-outline mt-3" onClick={() => setBooking(true)}>Agendar paciente</button>
      )}
      {booking && (
        <form onSubmit={bookPatient} className="mt-3 grid gap-3 border-t border-black/5 pt-3" aria-label="Agendar paciente">
          <input className="input" placeholder="Nombre y apellido" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} autoFocus />
          <div className="grid gap-3 sm:grid-cols-2">
            <input className="input" type="tel" inputMode="tel" placeholder="WhatsApp / teléfono (opcional)" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
            <input className="input" type="email" placeholder="Email (opcional)" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <input className="input" placeholder="Notas (opcional)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          {err && <p role="alert" className="text-sm text-red-700">{err}</p>}
          <div className="flex gap-2">
            <button type="submit" className="btn btn-md btn-primary" disabled={busy}>{busy ? 'Agendando…' : 'Agendar'}</button>
            <button type="button" className="btn btn-md btn-outline" onClick={() => { setBooking(false); setErr(''); }}>Cancelar</button>
          </div>
        </form>
      )}
    </li>
  );
};

/** The professional's agenda: for each day, the slots they offer (from their room rentals) and who took each one. */
interface AgendaProps { professionalId?: number | string; title?: string; subtitle?: string }

const ProfessionalAgenda: React.FC<AgendaProps> = ({ professionalId, title = 'Mi agenda', subtitle = 'Quién reservó cada horario de los consultorios que alquilaste.' }) => {
  const [month, setMonth] = useState(new Date());
  const [agenda, setAgenda] = useState<Agenda | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [selected, setSelected] = useState<string | null>(null);
  const detailRef = useRef<HTMLDivElement>(null);

  const monthKey = format(month, 'yyyy-MM');
  const todayKey = clinicDateKey(new Date());

  const load = useCallback(async () => {
    setState('loading');
    try {
      const r = await api.get('/postgres/professionals/agenda', {
        params: { from: format(startOfMonth(month), 'yyyy-MM-dd'), to: format(endOfMonth(month), 'yyyy-MM-dd'), professionalId },
      });
      setAgenda(r.data);
      setState('ok');
    } catch {
      setAgenda(null);
      setState('error');
    }
  }, [month, professionalId]);
  useEffect(() => { load(); }, [load]);

  const byDate = useMemo(() => new Map((agenda?.days || []).map(d => [d.date, d])), [agenda]);

  // pick a sensible day when the month loads: today, else the next day with slots, else the first one
  useEffect(() => {
    if (!agenda) return;
    const keys = agenda.days.map(d => d.date);
    setSelected(prev => (prev && prev.startsWith(monthKey) && byDate.has(prev)) ? prev
      : keys.includes(todayKey) ? todayKey
      : keys.find(k => k >= todayKey) || keys[0] || null);
  }, [agenda, monthKey, todayKey, byDate]);

  const days = useMemo(() => eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) }), [month]);
  const lead = (startOfMonth(month).getDay() + 6) % 7;            // weeks start on Monday
  const totals = useMemo(() => (agenda?.days || []).reduce((t, d) => ({
    free: t.free + d.counts.free, pending: t.pending + d.counts.pending,
    taken: t.taken + d.counts.confirmed + d.counts.completed,
  }), { free: 0, pending: 0, taken: 0 }), [agenda]);

  const day = selected ? byDate.get(selected) : undefined;

  // on phones the day's detail sits below the calendar: bring it into view after a tap
  const pick = (key: string) => {
    setSelected(key);
    if (window.matchMedia('(max-width: 1023px)').matches) setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn btn-outline btn-md" aria-label="Mes anterior" onClick={() => setMonth(subMonths(month, 1))}><ChevronLeft className="h-4 w-4" /></button>
          <span className="min-w-[9rem] text-center font-semibold capitalize">{format(month, 'MMMM yyyy', { locale: es })}</span>
          <button className="btn btn-outline btn-md" aria-label="Mes siguiente" onClick={() => setMonth(addMonths(month, 1))}><ChevronRight className="h-4 w-4" /></button>
          <button className="btn btn-secondary btn-md" onClick={() => setMonth(new Date())}>Hoy</button>
        </div>
      </div>

      {state === 'ok' && (
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="rounded-xl border border-green-200 bg-green-50 p-3"><div className="text-2xl font-bold text-green-700">{totals.taken}</div><div className="text-xs text-green-700">Reservados</div></div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3"><div className="text-2xl font-bold text-amber-800">{totals.pending}</div><div className="text-xs text-amber-800">Por aprobar</div></div>
          <div className="rounded-xl border border-gray-200 bg-white p-3"><div className="text-2xl font-bold text-gray-700">{totals.free}</div><div className="text-xs text-gray-600">Libres</div></div>
        </div>
      )}

      {state === 'loading' && <div className="animate-pulse rounded-2xl bg-gray-100 h-64" />}
      {state === 'error' && <div className="card p-8 text-center text-gray-500">No pudimos cargar tu agenda. <button className="underline font-semibold" onClick={load}>Reintentar</button></div>}

      {state === 'ok' && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
          <div className="card p-4">
            <div className="mb-2 grid grid-cols-7 text-center text-xs font-bold uppercase tracking-wide text-gray-400">
              {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => <div key={i}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1" role="grid" aria-label={`Calendario de ${format(month, 'MMMM yyyy', { locale: es })}`}>
              {Array.from({ length: lead }).map((_, i) => <div key={`b${i}`} />)}
              {days.map(d => {
                const key = format(d, 'yyyy-MM-dd');
                const info = byDate.get(key);
                const total = info ? info.counts.free + info.counts.pending + info.counts.confirmed + info.counts.completed : 0;
                const taken = info ? info.counts.pending + info.counts.confirmed + info.counts.completed : 0;
                const isSel = key === selected;
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={!info}
                    onClick={() => pick(key)}
                    aria-label={info ? `${format(d, "d 'de' MMMM", { locale: es })}: ${taken} de ${total} horarios ocupados` : `${format(d, "d 'de' MMMM", { locale: es })}: sin consultorio`}
                    aria-pressed={isSel}
                    className={`flex aspect-square flex-col items-center justify-center rounded-xl border text-sm transition-colors
                      ${isSel ? 'border-primary-600 bg-primary-600 text-white' : info ? 'border-gray-200 bg-white hover:border-primary-300' : 'border-transparent text-gray-300'}
                      ${key === todayKey && !isSel ? 'ring-2 ring-accent-gold' : ''}`}
                  >
                    <span className="font-semibold">{format(d, 'd')}</span>
                    {info && total === 0 && info.other.length > 0 && (
                      <span className={`text-[11px] leading-none ${isSel ? 'text-white/90' : 'text-gray-500'}`}>{info.other.length} fuera</span>
                    )}
                    {info && total > 0 && (
                      <span className={`text-[11px] leading-none ${isSel ? 'text-white/90' : 'text-gray-500'}`}>
                        {info.counts.pending > 0 && <span className={`mr-0.5 inline-block h-1.5 w-1.5 rounded-full ${isSel ? 'bg-accent-gold' : 'bg-amber-500'}`} aria-hidden="true" />}
                        {taken}/{total}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-gray-500">Cada día muestra horarios ocupados / totales. <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" /> hay reservas por aprobar.</p>
          </div>

          <div className="min-w-0" ref={detailRef}>
            {!agenda || agenda.days.length === 0 ? (
              <div className="card p-10 text-center">
                <CalendarClock className="mx-auto mb-3 h-10 w-10 text-gray-300" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-gray-900">No tenés consultorios reservados este mes</h3>
                <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500">Los pacientes solo pueden reservar dentro de los horarios en que alquilás un consultorio. Reservá horas en “Alquiler de Salas” y van a aparecer acá.</p>
              </div>
            ) : !day ? (
              <div className="card p-10 text-center text-gray-500">Elegí un día del calendario para ver sus horarios.</div>
            ) : (
              <div className="card p-5">
                <h3 className="text-xl font-semibold text-gray-900">{upper(format(dateFromKey(day.date), "EEEE d 'de' MMMM", { locale: es }))}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {day.rentals.length > 0 ? `Turnos de ${agenda.durationMinutes} min en tus consultorios:` : 'Sin consultorio alquilado este día'}
                </p>
                {day.rentals.length > 0 && (
                  <ul className="mt-2 space-y-2">
                    {day.rentals.map(r => (
                      <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm">
                        <span className="font-medium text-gray-800">{r.roomName} · {r.start}–{r.end}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 text-sm">
                  <b className="text-green-700">{day.counts.confirmed + day.counts.completed} reservados</b>
                  {day.counts.pending > 0 && <> · <b className="text-amber-800">{day.counts.pending} por aprobar</b></>} · {day.counts.free} libres
                </p>
                <ul className="mt-4 space-y-3">
                  {day.slots.map(s => <SlotRow key={`${s.time}-${s.roomName}`} slot={s} onChanged={load} professionalId={professionalId} admin={!!professionalId} />)}
                </ul>
                {day.other.length > 0 && (<>
                  <h4 className="mt-6 text-sm font-bold uppercase tracking-wide text-gray-500">Otros turnos del día (fuera de tus alquileres)</h4>
                  <ul className="mt-2 space-y-3">{day.other.map(s => <SlotRow key={`o-${s.time}-${s.appointment?.id}`} slot={s} onChanged={load} professionalId={professionalId} admin={!!professionalId} />)}</ul>
                </>)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalAgenda;
